import React from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { TableVirtuoso } from 'react-virtuoso';
import { Icon, Dropdown, Menu, Modal, Button, Tooltip, Tag } from 'antd';
import moment from 'moment';
import 'moment/locale/pt-br';

import { department, frequency } from './Utils';
import { ModuleHeader } from '../ModuleHeader';
import { HeaderContainer, HeaderLeftContent } from './styles';
import Search from '~/_shared/components/Search';
import TaskGroups from '~/_domains/tasks/TaskGroups';
import {
  taskRuleStatus, TASK_RULE_SET, TASK_RULE_NONE, TASK_RULE_MISSING, NO_RULE_HELP, MISSING_RULE_HELP,
} from '~/_domains/tasks/enrollment';

// Filtro de regra: as pendências (sem regra) ficam a um clique, e as marcadas
// como "não possui regra" aparecem separadas, para conferir a decisão.
var RULE_FILTERS = [
  { value: TASK_RULE_MISSING, label: 'Sem regra', color: '#d46b08', bg: '#fff7e6', help: MISSING_RULE_HELP },
  { value: TASK_RULE_NONE, label: 'Não possui regra', color: '#531dab', bg: '#f9f0ff', help: NO_RULE_HELP },
  { value: TASK_RULE_SET, label: 'Com regra', color: '#389e0d', bg: '#f6ffed', help: 'Têm regra própria ou do grupo: o sistema as sugere para os clientes que combinam.' },
];

// Helper function to format dates as DD/MM
var formatDate = function(dateString) {
  if (!dateString) return '';
  var parts = dateString.split('-');
  if (parts.length === 3) {
    var day = parts[2].split('T')[0];
    var month = parts[1];
    return day.padStart(2, '0') + '/' + month.padStart(2, '0');
  }
  var date = new Date(dateString);
  var dd = String(date.getDate()).padStart(2, '0');
  var mm = String(date.getMonth() + 1).padStart(2, '0');
  return dd + '/' + mm;
};

var getTaskDueDates = function(task) {
  var dates = [];
  if (task.first_date) dates.push(formatDate(task.first_date));
  if (task.second_date) dates.push(formatDate(task.second_date));
  if (task.third_date) dates.push(formatDate(task.third_date));
  if (task.fourth_date) dates.push(formatDate(task.fourth_date));
  return dates;
};

var getFrequencyLabel = function(frequencyValue, dates) {
  if (!dates || dates.length === 0) return frequency(frequencyValue);
  switch (parseInt(frequencyValue)) {
    case 1: return 'Mensal em ' + dates[0];
    case 2: return 'Trimestral em ' + dates.join(', ');
    case 3: return 'Semestral em ' + dates.join(' e ');
    case 4: return 'Anual em ' + dates[0];
    default: return frequency(frequencyValue);
  }
};

var COL = {
  name: null,
  department: 160,
  frequency: 220,
  updated_at: 130,
  clients: 80,
  actions: 100
};
var FIXED_W = COL.department + COL.frequency + COL.updated_at + COL.clients + COL.actions;

var thBase = {
  padding: '10px 12px', background: '#fafafa',
  borderBottom: '1px solid #e8e8e8',
  borderRight: '1px solid #e8e8e8',
  fontWeight: 600, fontSize: 13, textAlign: 'left',
  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  color: '#595959'
};
var tdBase = {
  padding: '10px 12px', background: '#fff',
  borderBottom: '1px solid #f0f0f0',
  borderRight: '1px solid #f0f0f0',
  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  fontSize: 13
};

function thS(w, extra) { var s = Object.assign({}, thBase, extra); if (w) s.width = w; return s; }
function tdS(w, extra) { var s = Object.assign({}, tdBase, extra); if (w) s.width = w; return s; }

var SORTERS = {
  name: function(a, b) { return (a.name || '').localeCompare(b.name || ''); },
  department: function(a, b) { return (a.department || '').localeCompare(b.department || ''); },
  frequency: function(a, b) { return (a.frequency || 0) - (b.frequency || 0); },
  clients: function(a, b) { return (a.registers_count || 0) - (b.registers_count || 0); },
  updated_at: function(a, b) { return new Date(a.updated_at || 0) - new Date(b.updated_at || 0); }
};

var DEPT_FILTERS = [
  { value: '1', label: department(1), color: '#722ed1', bg: '#f9f0ff' },
  { value: '2', label: department(2), color: '#1890ff', bg: '#e6f7ff' },
  { value: '3', label: department(3), color: '#13c2c2', bg: '#e6fffb' },
  { value: '4', label: department(4), color: '#52c41a', bg: '#f6ffed' },
  { value: '5', label: department(5), color: '#fa8c16', bg: '#fff7e6' },
  { value: '6', label: department(6), color: '#eb2f96', bg: '#fff0f6' }
];

var FREQ_FILTERS = [
  { value: 0, label: frequency(0) },
  { value: 1, label: frequency(1) },
  { value: 2, label: frequency(2) },
  { value: 3, label: frequency(3) },
  { value: 4, label: frequency(4) }
];

var List = React.memo(function List(props) {
  var history = useHistory();
  var tasks = props.tasks;
  var isLoading = props.isLoading;
  var isModalVisible = props.isModalVisible;
  var isCustomersModalVisible = props.isCustomersModalVisible;

  var searchState = React.useState('');
  var search = searchState[0];
  var setSearch = searchState[1];

  var sortState = React.useState('name');
  var sortField = sortState[0];
  var setSortField = sortState[1];

  var orderState = React.useState('ascend');
  var sortOrder = orderState[0];
  var setSortOrder = orderState[1];

  var deptFilterState = React.useState(null);
  var deptFilter = deptFilterState[0];
  var setDeptFilter = deptFilterState[1];

  var ruleFilterState = React.useState(null);
  var ruleFilter = ruleFilterState[0];
  var setRuleFilter = ruleFilterState[1];

  var groupsOpenState = React.useState(false);
  var groupsOpen = groupsOpenState[0];
  var setGroupsOpen = groupsOpenState[1];
  var freqFilterState = React.useState(null);
  var freqFilter = freqFilterState[0];
  var setFreqFilter = freqFilterState[1];

  var onAction = function(e, task) {
    switch (e.key) {
      case 'customers':
        props.openCustomers(task);
        break;
      case 'edit':
        props.showModal(task);
        break;
      case 'delete':
        Modal.confirm({
          title: 'Você realmente deseja excluir esta tarefa?',
          content: 'Esta ação não poderá ser desfeita.',
          okText: 'Sim',
          okType: 'danger',
          cancelText: 'Cancelar',
          onOk: function() { props.deleteTask(task.id); }
        });
        break;
      default: break;
    }
  };

  var toggleSort = function(field) {
    if (sortField === field) {
      setSortOrder(sortOrder === 'ascend' ? 'descend' : 'ascend');
    } else {
      setSortField(field);
      setSortOrder('ascend');
    }
  };

  var externalSearch = props.externalSearch || '';

  var processedData = React.useMemo(function() {
    var list = tasks || [];

    // Search filter (internal + external)
    var combinedSearch = search || externalSearch;
    if (combinedSearch) {
      var term = combinedSearch.toLowerCase();
      list = list.filter(function(t) {
        return (t.name || '').toLowerCase().indexOf(term) >= 0;
      });
    }

    // Department filter
    if (deptFilter) {
      list = list.filter(function(t) {
        return String(t.department) === deptFilter;
      });
    }

    // Regra: tarefas desativadas não são pendência de ninguém
    if (ruleFilter) {
      list = list.filter(function(t) {
        return !t.disabled_at && taskRuleStatus(t) === ruleFilter;
      });
    }

    // Frequency filter
    if (freqFilter !== null) {
      list = list.filter(function(t) {
        return t.frequency === freqFilter;
      });
    }

    // Sort
    var sorter = SORTERS[sortField];
    if (sorter) {
      list = list.slice().sort(function(a, b) {
        var r = sorter(a, b);
        return sortOrder === 'descend' ? -r : r;
      });
    }

    return list;
  }, [tasks, search, externalSearch, deptFilter, freqFilter, ruleFilter, sortField, sortOrder]);

  var dataRef = React.useRef({ processedData: processedData });
  dataRef.current.processedData = processedData;

  var sortIcon = function(field) {
    if (sortField !== field) {
      return React.createElement(Icon, { type: 'caret-up', style: { fontSize: 10, color: '#bfbfbf', marginLeft: 4 } });
    }
    return React.createElement(Icon, {
      type: sortOrder === 'ascend' ? 'caret-up' : 'caret-down',
      style: { fontSize: 10, color: '#1890ff', marginLeft: 4 }
    });
  };

  var tableComponents = React.useMemo(function() {
    return {
      Table: function(p) {
        return React.createElement('table', Object.assign({}, p, {
          style: Object.assign({}, p.style, {
            width: '100%',
            tableLayout: 'fixed',
            borderCollapse: 'collapse'
          })
        }));
      },
      TableRow: function(p) {
        return React.createElement('tr', Object.assign({}, p, {
          className: 'row-clabs' + (p.className ? ' ' + p.className : ''),
          style: Object.assign({}, p.style, { cursor: 'default' })
        }));
      }
    };
  }, []);

  var fixedHeaderContent = React.useCallback(function() {
    return React.createElement('tr', null,
      React.createElement('th', {
        style: thS(COL.name, { cursor: 'pointer', position: 'sticky', left: 0, zIndex: 3, borderLeft: '1px solid #e8e8e8', minWidth: 180 }),
        onClick: function() { toggleSort('name'); }
      }, 'Nome da Tarefa', sortIcon('name')),
      React.createElement('th', {
        style: thS(COL.department, { cursor: 'pointer' }),
        onClick: function() { toggleSort('department'); }
      },
        'Departamento', sortIcon('department'),
        deptFilter && React.createElement(Tooltip, { title: 'Limpar filtro' },
          React.createElement(Icon, {
            type: 'close-circle', theme: 'filled',
            style: { marginLeft: 6, fontSize: 12, color: '#ff4d4f', cursor: 'pointer' },
            onClick: function(e) { e.stopPropagation(); setDeptFilter(null); }
          })
        ),
        React.createElement(Dropdown, {
          trigger: ['click'],
          overlay: React.createElement(Menu, {
            onClick: function(e) { setDeptFilter(e.key === deptFilter ? null : e.key); }
          },
            DEPT_FILTERS.map(function(f) {
              return React.createElement(Menu.Item, {
                key: f.value,
                style: deptFilter === f.value ? { background: '#e6f7ff', fontWeight: 500 } : {}
              }, f.label);
            })
          )
        },
          React.createElement(Icon, {
            type: 'filter', theme: deptFilter ? 'filled' : 'outlined',
            style: { marginLeft: 4, fontSize: 11, color: deptFilter ? '#1890ff' : '#bfbfbf', cursor: 'pointer' }
          })
        )
      ),
      React.createElement('th', {
        style: thS(COL.frequency, { cursor: 'pointer' }),
        onClick: function() { toggleSort('frequency'); }
      },
        'Periodicidade', sortIcon('frequency'),
        freqFilter !== null && React.createElement(Tooltip, { title: 'Limpar filtro' },
          React.createElement(Icon, {
            type: 'close-circle', theme: 'filled',
            style: { marginLeft: 6, fontSize: 12, color: '#ff4d4f', cursor: 'pointer' },
            onClick: function(e) { e.stopPropagation(); setFreqFilter(null); }
          })
        ),
        React.createElement(Dropdown, {
          trigger: ['click'],
          overlay: React.createElement(Menu, {
            onClick: function(e) { setFreqFilter(freqFilter === Number(e.key) ? null : Number(e.key)); }
          },
            FREQ_FILTERS.map(function(f) {
              return React.createElement(Menu.Item, {
                key: f.value,
                style: freqFilter === f.value ? { background: '#e6f7ff', fontWeight: 500 } : {}
              }, f.label);
            })
          )
        },
          React.createElement(Icon, {
            type: 'filter', theme: freqFilter !== null ? 'filled' : 'outlined',
            style: { marginLeft: 4, fontSize: 11, color: freqFilter !== null ? '#1890ff' : '#bfbfbf', cursor: 'pointer' }
          })
        )
      ),
      React.createElement('th', {
        style: thS(COL.updated_at, { cursor: 'pointer' }),
        onClick: function() { toggleSort('updated_at'); }
      }, 'Atualizado', sortIcon('updated_at')),
      React.createElement('th', {
        style: thS(COL.clients, { textAlign: 'center', cursor: 'pointer' }),
        onClick: function() { toggleSort('clients'); }
      }, 'Clientes', sortIcon('clients')),
      React.createElement('th', { style: thS(COL.actions, { textAlign: 'center', position: 'sticky', right: 0, zIndex: 3, boxShadow: '-3px 0 6px rgba(0,0,0,0.06)' }) }, '')
    );
  }, [sortField, sortOrder, deptFilter, freqFilter]);

  var itemContent = React.useCallback(function(index) {
    var d = dataRef.current;
    var record = d.processedData[index];
    if (!record) return null;

    var dueDates = getTaskDueDates(record);
    var freqLabel = getFrequencyLabel(record.frequency, dueDates);

    return React.createElement(React.Fragment, null,
      React.createElement('td', {
        style: tdS(COL.name, { fontWeight: 500, position: 'sticky', left: 0, zIndex: 1, borderLeft: '1px solid #f0f0f0', minWidth: 180 })
      },
        record.name,
        // Grupo de tarefas (TFE e afins) a que a tarefa pertence
        record.group
          ? React.createElement(Tooltip, { title: 'Grupo ' + record.group.name },
              React.createElement(Icon, { type: 'folder', theme: 'filled', 'aria-label': 'Grupo ' + record.group.name, 'data-group-icon': record.group.name, style: { color: '#faad14', fontSize: 13, marginLeft: 6 } }))
          : null,
        // Situação da regra: só as que pedem atenção ou foram marcadas à parte
        !record.disabled_at && taskRuleStatus(record) === TASK_RULE_MISSING
          ? React.createElement(Tooltip, { title: MISSING_RULE_HELP, overlayStyle: { maxWidth: 320 } },
              React.createElement(Tag, { color: 'orange', style: { marginLeft: 6, fontSize: 11, lineHeight: '18px' }, 'data-rule-tag': 'missing' }, 'sem regra'))
          : null,
        !record.disabled_at && taskRuleStatus(record) === TASK_RULE_NONE
          ? React.createElement(Tooltip, { title: NO_RULE_HELP, overlayStyle: { maxWidth: 320 } },
              React.createElement(Tag, { color: 'purple', style: { marginLeft: 6, fontSize: 11, lineHeight: '18px' }, 'data-rule-tag': 'none' }, 'não possui regra'))
          : null,
        // Marca discreta de quem entra na Conferência, para não precisar abrir
        // tarefa por tarefa só para descobrir isso
        record.show_in_conference
          ? React.createElement(Tooltip, { title: 'Aparece no módulo Conferência' },
              React.createElement(Icon, { type: 'audit', style: { color: '#722ed1', fontSize: 12, marginLeft: 6 } })
            )
          : null
      ),
      React.createElement('td', { style: tdS(COL.department) }, department(record.department)),
      React.createElement('td', { style: tdS(COL.frequency) },
        React.createElement('span', { style: { display: 'flex', alignItems: 'center', gap: 6 } },
          React.createElement(Icon, { type: 'sync', style: { color: '#1890ff', fontSize: 12 } }),
          freqLabel
        )
      ),
      React.createElement('td', { style: tdS(COL.updated_at, { color: '#8c8c8c' }) },
        record.updated_at
          ? React.createElement(Tooltip, { title: moment(record.updated_at).format('DD/MM/YYYY [às] HH:mm:ss') },
              React.createElement('span', null, moment(record.updated_at).locale('pt-br').fromNow())
            )
          : '-'
      ),
      React.createElement('td', {
        style: tdS(COL.clients, { textAlign: 'center', cursor: 'pointer' }),
        onClick: function() { props.openCustomers(record); }
      },
        React.createElement('span', {
          style: {
            display: 'inline-flex', alignItems: 'center', gap: 4,
            color: (record.registers_count || 0) > 0 ? '#1890ff' : '#bfbfbf',
            fontSize: 13, fontWeight: 500
          }
        },
          React.createElement(Icon, { type: 'team', style: { fontSize: 12 } }),
          record.registers_count || 0
        )
      ),
      React.createElement('td', {
        style: tdS(COL.actions, { textAlign: 'center', position: 'sticky', right: 0, zIndex: 1, boxShadow: '-3px 0 6px rgba(0,0,0,0.06)' })
      },
        React.createElement(Dropdown, {
          overlayStyle: { zIndex: 1020 },
          trigger: ['click'],
          overlay: React.createElement(Menu, {
            onClick: function(e) { onAction(e, record); }
          },
            React.createElement(Menu.Item, { key: 'customers' },
              React.createElement(Icon, { type: 'team' }), ' Clientes'
            ),
            React.createElement(Menu.Item, { key: 'edit' },
              React.createElement(Icon, { type: 'edit' }), ' Editar'
            ),
            React.createElement(Menu.Divider, null),
            React.createElement(Menu.Item, { key: 'delete' },
              React.createElement(Icon, { type: 'delete' }), ' Excluir'
            )
          )
        },
          React.createElement('a', { style: { color: '#1890ff', fontSize: 13 } },
            'Ações ', React.createElement(Icon, { type: 'down' })
          )
        )
      )
    );
  }, []);

  var tableHeight = props.embedded ? (window.innerHeight - 216) : (window.innerHeight - 276);

  // Hooks precisam vir antes de qualquer return condicional — um return de
  // loading acima de um useMemo muda a contagem de hooks entre renders e
  // derruba o componente ("Rendered more hooks than during the previous render")
  var deptCounts = React.useMemo(function() {
    var counts = {};
    var all = tasks || [];
    DEPT_FILTERS.forEach(function(d) { counts[d.value] = 0; });
    all.forEach(function(t) {
      var key = String(t.department);
      if (counts[key] !== undefined) counts[key]++;
    });
    counts['all'] = all.length;
    return counts;
  }, [tasks]);

  var ruleCounts = React.useMemo(function() {
    var counts = {};
    RULE_FILTERS.forEach(function(f) { counts[f.value] = 0; });
    (tasks || []).forEach(function(t) {
      if (t.disabled_at) return;
      counts[taskRuleStatus(t)]++;
    });
    return counts;
  }, [tasks]);

  if (isLoading) {
    return React.createElement('div', {
      style: { display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }
    },
      React.createElement(Icon, { type: 'loading', style: { fontSize: 24, color: '#1890ff' } })
    );
  }

  var headerEl = props.embedded ? null : (
    React.createElement(ModuleHeader, {
      breadcrumbs: ['Tarefas', 'Cadastro de tarefas'],
      title: 'Cadastro de Tarefas',
      search: React.createElement(Search, {
        value: search,
        placeholder: 'Pesquisar tarefas',
        onChange: function(e) { setSearch(e.target.value); }
      }),
      actions: React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8 } },
        React.createElement('span', { style: { fontSize: 13, color: '#8c8c8c' } },
          processedData.length + ' tarefa' + (processedData.length !== 1 ? 's' : '')
        ),
        React.createElement(Tooltip, { title: 'Grupos de tarefas' },
          React.createElement(Button, { icon: 'folder', shape: 'circle', onClick: function() { setGroupsOpen(true); }, 'data-open-groups': true })
        ),
        React.createElement(Tooltip, { title: 'Matriz Clientes x Tarefas' },
          React.createElement(Button, { icon: 'table', shape: 'circle', onClick: function() { history.push('/tarefas/start'); } })
        ),
        React.createElement(Button, { icon: 'plus', shape: 'circle', onClick: function() { props.showModal(); } })
      )
    })
  );

  var deptPillStyle = function(f, isActive) {
    return {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      padding: '6px 14px',
      borderRadius: 20,
      fontSize: 13,
      fontWeight: isActive ? 600 : 400,
      cursor: 'pointer',
      transition: 'all 0.15s',
      border: '1px solid ' + (isActive ? f.color : '#e8e8e8'),
      background: isActive ? f.bg : '#fff',
      color: isActive ? f.color : '#595959',
    };
  };

  var allPillStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 14px',
    borderRadius: 20,
    fontSize: 13,
    fontWeight: !deptFilter ? 600 : 400,
    cursor: 'pointer',
    transition: 'all 0.15s',
    border: '1px solid ' + (!deptFilter ? '#1890ff' : '#e8e8e8'),
    background: !deptFilter ? '#e6f7ff' : '#fff',
    color: !deptFilter ? '#1890ff' : '#595959',
  };

  var deptPillsEl = React.createElement('div', {
    style: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }
  },
    React.createElement('span', {
      style: allPillStyle,
      onClick: function() { setDeptFilter(null); }
    },
      'Todos',
      React.createElement('span', {
        style: { fontSize: 11, fontWeight: 600, opacity: 0.7 }
      }, deptCounts['all'])
    ),
    DEPT_FILTERS.map(function(f) {
      var isActive = deptFilter === f.value;
      return React.createElement('span', {
        key: f.value,
        style: deptPillStyle(f, isActive),
        onClick: function() { setDeptFilter(isActive ? null : f.value); }
      },
        React.createElement('span', {
          style: {
            width: 8, height: 8, borderRadius: '50%',
            background: f.color, flexShrink: 0
          }
        }),
        f.label,
        React.createElement('span', {
          style: { fontSize: 11, fontWeight: 600, opacity: 0.7 }
        }, deptCounts[f.value])
      );
    })
  );

  var missingCount = ruleCounts[TASK_RULE_MISSING];
  var ruleBarEl = React.createElement('div', {
    'data-rule-bar': true,
    style: {
      display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 12, padding: '10px 14px',
      borderRadius: 10, border: '1px solid ' + (missingCount > 0 ? '#ffd591' : '#d9f7be'),
      background: missingCount > 0 ? '#fff7e6' : '#f6ffed',
    }
  },
    React.createElement(Icon, { type: missingCount > 0 ? 'thunderbolt' : 'check-circle', style: { color: missingCount > 0 ? '#d46b08' : '#389e0d', fontSize: 16 } }),
    React.createElement('span', { style: { flex: '1 1 320px', fontSize: 13, color: '#262626' } },
      missingCount > 0
        ? React.createElement('span', null,
            React.createElement('strong', null, missingCount + (missingCount === 1 ? ' tarefa sem regra' : ' tarefas sem regra')),
            ': o sistema não sabe para quem sugeri-las. Defina quem recebe ou marque “Não possui regra”.')
        : 'Todas as tarefas ativas têm regra ou foram marcadas como “não possui regra”.'
    ),
    RULE_FILTERS.map(function(f) {
      var isActive = ruleFilter === f.value;
      return React.createElement(Tooltip, { key: f.value, title: f.help, overlayStyle: { maxWidth: 320 } },
        React.createElement('button', {
          type: 'button',
          'data-rule-filter': f.value,
          'aria-pressed': isActive,
          onClick: function() { setRuleFilter(isActive ? null : f.value); },
          style: {
            display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 12px', borderRadius: 16, cursor: 'pointer',
            fontSize: 12, fontWeight: isActive ? 600 : 400, fontFamily: 'inherit',
            border: '1px solid ' + (isActive ? f.color : '#e8e8e8'),
            background: isActive ? f.bg : '#fff', color: isActive ? f.color : '#595959',
          }
        }, f.label, React.createElement('span', { style: { fontSize: 11, fontWeight: 600, opacity: 0.7 } }, ruleCounts[f.value]))
      );
    })
  );

  return React.createElement('div', null,
    headerEl,
    ruleBarEl,
    React.createElement(TaskGroups, { visible: groupsOpen, onClose: function() { setGroupsOpen(false); }, tasks: tasks }),
    deptPillsEl,
    processedData.length === 0 ? (
      React.createElement('div', {
        style: {
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', height: 300, color: '#bfbfbf'
        }
      },
        React.createElement(Icon, { type: 'inbox', style: { fontSize: 48, marginBottom: 16 } }),
        React.createElement('div', { style: { fontSize: 14 } }, 'Nenhuma tarefa encontrada')
      )
    ) : (
      React.createElement('div', { className: 'table-clabs' },
        React.createElement(TableVirtuoso, {
          style: { height: tableHeight, border: '1px solid #e8e8e8', borderRadius: 4 },
          data: processedData,
          components: tableComponents,
          fixedHeaderContent: fixedHeaderContent,
          itemContent: itemContent,
          overscan: 20
        })
      )
    )
  );
});

var mapStateToProps = function(state) {
  return {
    tasks: state.tasks.list,
    isLoading: state.tasks.isLoading
  };
};

var mapDispatchProps = function(dispatch) {
  return {
    deleteTask: function(id) {
      dispatch({
        type: 'DELETE_TASK',
        payload: {
          request: {
            method: 'delete',
            url: '/task/' + id
          }
        },
        id: id
      });
    }
  };
};

export default connect(mapStateToProps, mapDispatchProps)(List);
